import { z } from 'zod'

import { getEntityRegistry } from '../../engine/entity-registry'
import {
  expandConditionsToFlatSets,
  findReadonlyFilterFields,
  normalizeEnumCondition,
  normalizeValueForOperator,
  pickEditableStatusField,
} from '../filter-normalizer'
import {
  requireDataClassName,
  resolveDataClass,
  serializeDataItems,
  unknownEntityError,
} from '../shared/entity-serializer'
import type { SerializedItem } from '../shared/entity-serializer'
import { encodeFilterQuery } from '../shared/filterQuery'
import {
  isQueryValidationError,
  orderArg,
  warnOrderDowngraded,
} from '../shared/orderArg'
import type { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder/types'
import { AI_CHATBOT_CONFIG } from '@/Atomic/Widgets/AiChatBot/core/engine/config'
import type { AdapterSchemaAttributes } from '@/Atomic/Widgets/AiChatBot/core/engine/contracts'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

/**
 * Wraps the returned rows with completeness metadata so the model never has to
 * guess whether the set is a full result or a capped page — a bare array leaves
 * it to invent a total ("100 open, capped") when far fewer rows came back.
 * `truncated: true` means the cap was hit and more records may exist (the model
 * should call count_entities for the real total); `false` means these ARE all the
 * matching records. Schema-agnostic pagination metadata — standard tool-result shape.
 */
function buildListResult(items: SerializedItem[], limit: number) {
  return {
    items,
    returnedCount: items.length,
    limit,
    truncated: items.length >= limit,
  }
}

export const listEntitiesTool = defineTool({
  name: 'list_entities',
  description:
    'List CRM entities with optional filters, order, and limit. Use this to find records by criteria. ' +
    'For enum fields like `status`, pass the human-readable label exactly as it appears in the schema (call get_schema if unsure) — never guess or hardcode raw PISA codes. Labels (and status-group names like "in progress") are resolved to codes via the schema. A value that matches nothing (e.g. a semantic term like "open" that is not an actual status) returns an error listing the valid labels instead of silently matching zero records — so never treat an empty result as "none match" when the filter value was a guess; present the valid options to the user instead.',
  inputSchema: () =>
    z.object({
      dataClass: z
        .string()
        .describe(
          'Entity type, e.g. Company, Person, Ticket, Opportunity, Order, Quote, Activity, Lead, Timerecord',
        ),
      filters: z
        .record(z.string(), z.unknown())
        .optional()
        .describe('Filter key-value pairs to pass to the DataClass query'),
      order: orderArg.optional().describe('Sort order'),
      limit: z
        .number()
        .optional()
        .describe(
          `Max items to return (default ${AI_CHATBOT_CONFIG.queryDefaults.listLimit}, max ${AI_CHATBOT_CONFIG.queryDefaults.listLimitCap})`,
        ),
      scopeToCurrentUser: z
        .boolean()
        .optional()
        .describe(
          'When true, filters results to records belonging to the current user via userScopeFields',
        ),
    }),

  async run(args, ctx) {
    const {
      dataClass,
      filters = {},
      order,
      limit: rawLimit,
      scopeToCurrentUser,
    } = args

    if (!resolveDataClass(dataClass)) {
      return unknownEntityError(dataClass)
    }

    const dcName = requireDataClassName(dataClass)
    if (!dcName) return unknownEntityError(dataClass)

    const meta = getEntityRegistry()[dcName]

    const limit = Math.min(
      rawLimit ?? AI_CHATBOT_CONFIG.queryDefaults.listLimit,
      AI_CHATBOT_CONFIG.queryDefaults.listLimitCap,
    )

    const effectiveOrder = order ??
      meta?.defaultOrder ?? [['updatedAt', 'desc']]

    const effectiveFilters: Record<string, unknown> = { ...filters }
    if (scopeToCurrentUser && ctx.currentUserId && meta?.userScopeFields) {
      for (const field of meta.userScopeFields) {
        effectiveFilters[field] = ctx.currentUserId
      }
      if (meta.userScopeDefaults) {
        Object.assign(effectiveFilters, meta.userScopeDefaults)
      }
    }

    // Resolve enum display labels ("open", "active") to PISA codes so
    // natural-language filter values work — not just raw codes. Without this a
    // value like { status: 'open' } is sent verbatim and PISA rejects it with an
    // invalid-enum error. This instance may not have build_filter (it is gated
    // behind a feature flag), so list_entities must resolve enums itself rather
    // than delegating. Uses the same schema-driven normalizer as build_filter,
    // so there is one source of truth for label→code resolution. Skip the schema
    // load entirely when there is nothing to resolve.
    const { data } = getHostRuntime()
    let schemaAttributes: AdapterSchemaAttributes | undefined
    if (Object.keys(effectiveFilters).length > 0) {
      try {
        const result = await data.getSchema(data.getSchemaName(dataClass))
        schemaAttributes = result.schemaAttributes
      } catch {
        schemaAttributes = undefined
      }
    }

    // Only ENUM fields get label→code resolution. References carry ids already
    // (userScope + model-resolved records) — running them through the enum
    // normalizer would let a shared display title expand one id to same-named
    // siblings (wrong records), so they pass through untouched. Every other type
    // is an equality pass-through.
    //
    // A duplicate-title enum ("open"/"active") resolves to several codes and
    // returns an array; the flat `?field=` channel rejects arrays (ArgumentError),
    // so that query escalates to the structured `?query=` channel — the same
    // channel build_filter and the list page use, which expresses multi-value
    // `in` correctly. Single-value filters stay on the proven flat channel.
    let needsStructured = false
    const conditions: { field: string; op: string; value: unknown }[] = []
    const flatFilters: Record<string, unknown> = {}
    // Filters the model asked for that the backend cannot honour. Collected so the
    // tool fails loud with a pointer to the right field/value instead of sending a
    // bad filter — on the flat channel the backend SILENTLY IGNORES an unknown or
    // computed field and returns UNFILTERED rows, which the model then narrates as
    // if they matched ("2,472 open opportunities"); on the structured channel it
    // 422s. Only the model's own `filters` are validated — userScope keys injected
    // from the registry are trusted server filter keys.
    const filterErrors: string[] = []
    const modelProvidedFields = new Set(Object.keys(filters))
    // A readonly attribute is computed/system-maintained and is not a server-side
    // filter (e.g. Opportunity `open` is a readonly computed boolean — "open" really
    // means `status` in the open groups). Dates are the one readonly type that IS
    // filterable (createdAt/updatedAt), so allow those; reject the rest loud with a
    // pointer to the editable status field the user most likely meant. Schema-driven
    // and portable — any host that marks computed fields readonly gets the same guard.
    const readonlyBad = new Set(
      findReadonlyFilterFields(modelProvidedFields, schemaAttributes ?? {}),
    )
    const editableStatusField = pickEditableStatusField(schemaAttributes ?? {})
    for (const [field, value] of Object.entries(effectiveFilters)) {
      const attr = schemaAttributes?.[field]
      if (readonlyBad.has(field)) {
        filterErrors.push(
          `Field "${field}"${attr ? ` ("${attr.title}")` : ''} is a computed/readonly field and cannot be used as a filter.` +
            (editableStatusField
              ? ` To filter by state (open, active, won, etc.), use the editable "${editableStatusField}" field with a specific value instead — call get_schema for the valid values.`
              : ''),
        )
        continue
      }
      if (attr?.type === 'enum' && attr.enumValues?.length) {
        const normalized = normalizeEnumCondition(
          Array.isArray(value) ? 'in' : 'eq',
          value,
          attr.enumValues,
        )
        // Entries normalizeEnumCondition couldn't resolve fall through as raw
        // strings (not valid codes) — detect them the same way build_filter does.
        const validCodes = new Set(attr.enumValues.map((e) => e.value))
        const resolvedValues = Array.isArray(normalized.value)
          ? (normalized.value as unknown[])
          : normalized.value !== null
            ? [normalized.value]
            : []
        const unresolved = resolvedValues
          .map(String)
          .filter((v) => !validCodes.has(v))
        if (unresolved.length > 0) {
          const validLabels = [...new Set(attr.enumValues.map((e) => e.title))]
          // Point at another field that DOES recognize the value (e.g. "Lead" is
          // not a status but a valid `group`), so the model can correct the field
          // rather than drop the filter.
          const suggestions = unresolved
            .map((v) => {
              const lower = v.toLowerCase()
              const others = Object.entries(schemaAttributes ?? {})
                .filter(
                  ([key, a]) =>
                    key !== field &&
                    a.enumValues?.some(
                      (e) =>
                        e.value.toLowerCase() === lower ||
                        e.title.toLowerCase() === lower,
                    ),
                )
                .map(([key, a]) => `"${key}" ("${a.title}")`)
              return others.length > 0
                ? `"${v}" is a valid value for ${others.join(', ')} — the user may have meant that field.`
                : null
            })
            .filter((s): s is string => s !== null)
          filterErrors.push(
            `Field "${field}" ("${attr.title}"): ${unresolved
              .map((v) => `"${v}"`)
              .join(', ')} did not match any value. Valid options: ${validLabels
              .map((l) => `"${l}"`)
              .join(', ')}.` +
              (suggestions.length > 0 ? `\n${suggestions.join('\n')}` : ''),
          )
          continue
        }
        conditions.push({ field, op: normalized.op, value: normalized.value })
        if (Array.isArray(normalized.value)) {
          needsStructured = true
        } else {
          flatFilters[field] = normalized.value
        }
      } else if (attr?.type === 'reference') {
        // Flat channel keeps the scalar id (`?agent=<id>`). On the structured
        // channel a reference is always `in [id]` — byte-identical to what
        // build_filter and the manual FilterBuilder emit, so the same question
        // asked in chat and on the list page cannot return different sets.
        conditions.push({
          field,
          op: 'in',
          value: normalizeValueForOperator('in', value),
        })
        flatFilters[field] = value
      } else {
        conditions.push({ field, op: 'eq', value })
        flatFilters[field] = value
      }
    }

    if (filterErrors.length > 0) {
      return {
        error:
          `Cannot filter ${dataClass}. ${filterErrors.join(' ')} ` +
          'Present this to the user and ask which concrete field/value they mean — ' +
          'do NOT return unfiltered results as if they matched the filter.',
      }
    }

    const structure: SavedFilterStructure = { or: [{ and: conditions }] }

    const runQuery = (queryOrder: [string, 'asc' | 'desc'][] | undefined) =>
      needsStructured
        ? data.query(dataClass, {
            encodedFilterQuery: encodeFilterQuery(structure),
            order: queryOrder,
            limit,
          })
        : data.query(dataClass, {
            filters: flatFilters,
            order: queryOrder,
            limit,
          })

    // Flat-channel fallback for an instance whose structured `?query=` channel is
    // unavailable — it 422s on EVERY structured query, even a well-formed
    // multi-value one (seen on instances where advanced filtering is toggled on but
    // the backend endpoint is not). A multi-value filter like "open" (status in 6
    // codes) can't be sent flat in one request, so run one flat equality query per
    // value combination and merge — the flat `?field=value` endpoint is the basic
    // PISA read and works everywhere. Keeps the answer identical to a healthy
    // instance instead of dead-ending. Only reached when the structured channel is
    // genuinely broken (a persistent 422 after the order downgrade below).
    const runFlatFallback = async (): Promise<
      SerializedItem[] | { error: string }
    > => {
      const flatSets = expandConditionsToFlatSets(conditions, 25)
      if (!flatSets) {
        return {
          error:
            `Cannot list ${dataClass}: this instance does not support advanced (structured) filtering, and the request has too many value combinations to run on the basic filter channel. ` +
            'Ask the user to narrow the criteria (e.g. a single status value or a specific customer).',
        }
      }
      const runFlat = async (flat: Record<string, unknown>) => {
        try {
          return await data.query(dataClass, {
            filters: flat,
            order: effectiveOrder,
            limit,
          })
        } catch (err) {
          if (!isQueryValidationError(err)) {
            throw err
          }
          return data.query(dataClass, {
            filters: flat,
            order: undefined,
            limit,
          })
        }
      }
      const sets = await Promise.all(
        flatSets.map((flat) =>
          runFlat(flat).then((rows) =>
            serializeDataItems(rows, dataClass, ctx),
          ),
        ),
      )
      // Merge + dedupe by _id (a record satisfies only one combination for a
      // single-valued field like status; dedupe guards the general case), then
      // re-apply the requested order across the merged set and cap at `limit` —
      // each sub-query returned its own top-`limit`, so the global top-N needs a
      // re-sort.
      const byId = new Map<string, SerializedItem>()
      for (const set of sets) {
        for (const row of set) {
          byId.set(row._id, row)
        }
      }
      const sortable = (v: unknown): number | string | null => {
        if (v == null) {
          return null
        }
        if (v instanceof Date) {
          return v.getTime()
        }
        if (typeof v === 'number' || typeof v === 'string') {
          return v
        }
        if (typeof v === 'boolean') {
          return v ? 1 : 0
        }
        return String(v)
      }
      const merged = [...byId.values()].sort((a, b) => {
        for (const [field, dir] of effectiveOrder) {
          const av = sortable(a[field])
          const bv = sortable(b[field])
          let cmp = 0
          if (av == null && bv == null) {
            cmp = 0
          } else if (av == null) {
            cmp = -1
          } else if (bv == null) {
            cmp = 1
          } else if (typeof av === 'number' && typeof bv === 'number') {
            cmp = av - bv
          } else {
            cmp = String(av).localeCompare(String(bv))
          }
          if (cmp !== 0) {
            return dir === 'desc' ? -cmp : cmp
          }
        }
        return 0
      })
      return merged.slice(0, limit)
    }

    let items: unknown[]
    try {
      items = await runQuery(effectiveOrder)
    } catch (err) {
      // PISA returns 422 both when the order field is unsortable (e.g. a registry
      // defaultOrder naming a non-sortable field) AND when the structured `?query=`
      // channel is unavailable — indistinguishable by the error alone. Both arrive
      // as a wrapped DataScopeError ("A validation has failed") because `.take()`
      // routes through handleCommunicationError, so match on that, not on a raw
      // ClientError. Retry without order first (cheap, fixes the unsortable-order
      // case); rethrow network/auth errors rather than masking them.
      if (!isQueryValidationError(err)) {
        throw err
      }
      warnOrderDowngraded('list_entities', effectiveOrder)
      try {
        items = await runQuery(undefined)
      } catch (retryErr) {
        // A structured query that STILL 422s after dropping order means the
        // structured channel itself is unavailable, not the sort field — fall back
        // to the flat channel. Flat queries can only fail this way on a genuine
        // order issue, which has no fallback, so rethrow those.
        if (!needsStructured || !isQueryValidationError(retryErr)) {
          throw retryErr
        }
        const fallback = await runFlatFallback()
        return Array.isArray(fallback)
          ? buildListResult(fallback, limit)
          : fallback
      }
    }

    const serialized = await serializeDataItems(items, dataClass, ctx)
    return buildListResult(serialized, limit)
  },
})
